import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  MessageSquare,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Zap,
  Plane,
  MapPin,
  CalendarClock,
  Clock,
  Search,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import api from '@/services/api'

// Sample data for the chart
const queryData = [
  { date: 'Mon', queries: 124 },
  { date: 'Tue', queries: 186 },
  { date: 'Wed', queries: 152 },
  { date: 'Thu', queries: 203 },
  { date: 'Fri', queries: 178 },
  { date: 'Sat', queries: 62 },
  { date: 'Sun', queries: 45 },
]

interface DashboardSummary {
  fleet: {
    total_aircraft: number
    models: Record<string, number>
  }
  service_centers: {
    total: number
  }
  upcoming_phases: Array<{
    tail_number: string
    aircraft_model: string
    current_hours: number
    next_phase: number
    hours_until_next_phase: number
    urgency: string
  }>
  alerts: Array<{
    type: string
    message: string
    severity: string
  }>
}

export function DashboardPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null)

  useEffect(() => {
    api.get<DashboardSummary>('/api/dashboard/summary')
      .then(setSummary)
      .catch(() => {})
  }, [])

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening with your fleet.</p>
      </div>

      {/* Stats Grid */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Fleet Aircraft"
          value={summary?.fleet?.total_aircraft?.toString() || '0'}
          change={summary?.fleet?.models ? Object.entries(summary.fleet.models).map(([m, c]) => `${c} ${m}`).join(', ') : 'No aircraft registered'}
          changeType="neutral"
          icon={Plane}
        />
        <StatCard
          title="Service Centers"
          value={summary?.service_centers?.total?.toString() || '0'}
          change="Active locations"
          changeType="neutral"
          icon={MapPin}
        />
        <StatCard
          title="Upcoming Phases"
          value={summary?.upcoming_phases?.length?.toString() || '0'}
          change={summary?.upcoming_phases?.filter(p => p.urgency === 'critical').length ? `${summary.upcoming_phases.filter(p => p.urgency === 'critical').length} critical` : 'All on schedule'}
          changeType={summary?.upcoming_phases?.some(p => p.urgency === 'critical') ? 'negative' : 'positive'}
          icon={CalendarClock}
        />
        <StatCard
          title="Avg Response Time"
          value="1.2s"
          change="-0.3s"
          changeType="positive"
          icon={Zap}
        />
      </div>

      {/* Fleet Maintenance Status & Activity */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Upcoming Maintenance Phases */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Upcoming Maintenance Phases</CardTitle>
              <CardDescription>Fleet-wide ALIM phased maintenance schedule</CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/app/maintenance-program">
                View Program
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {summary?.upcoming_phases && summary.upcoming_phases.length > 0 ? (
              <div className="space-y-3">
                {summary.upcoming_phases.slice(0, 5).map((phase, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <div className={`rounded-full p-1.5 ${
                        phase.urgency === 'critical' ? 'bg-red-100' :
                        phase.urgency === 'high' ? 'bg-orange-100' :
                        'bg-blue-100'
                      }`}>
                        <Plane className={`h-4 w-4 ${
                          phase.urgency === 'critical' ? 'text-red-600' :
                          phase.urgency === 'high' ? 'text-orange-600' :
                          'text-blue-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{phase.tail_number}</p>
                        <p className="text-xs text-gray-500">{phase.aircraft_model} - Phase {phase.next_phase}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={
                        phase.urgency === 'critical' ? 'destructive' :
                        phase.urgency === 'high' ? 'warning' : 'secondary'
                      }>
                        {phase.hours_until_next_phase.toFixed(0)} hrs remaining
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={queryData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
                    <XAxis dataKey="date" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a365d',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="queries"
                      stroke="#2c5282"
                      strokeWidth={2}
                      dot={{ fill: '#2c5282', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card>
          <CardHeader>
            <CardTitle>Fleet Alerts</CardTitle>
            <CardDescription>Active notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {summary?.alerts && summary.alerts.length > 0 ? (
                summary.alerts.slice(0, 5).map((alert, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`mt-0.5 rounded-full p-1.5 ${
                      alert.severity === 'critical' ? 'bg-red-100 text-red-600' :
                      alert.severity === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      <AlertCircle className="h-3 w-3" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <p className="text-sm text-gray-900">{alert.message}</p>
                      <Badge variant="outline" className="text-xs">{alert.type}</Badge>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center py-8 text-gray-400">
                  <CheckCircle2 className="h-8 w-8 mb-2" />
                  <p className="text-sm">No active alerts</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Manual Status */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Button variant="outline" className="justify-start h-auto py-4" asChild>
              <Link to="/app/chat">
                <MessageSquare className="mr-3 h-5 w-5 text-voltaire-blue" />
                <div className="text-left">
                  <div className="font-medium">New Query</div>
                  <div className="text-xs text-gray-500">Ask a question</div>
                </div>
              </Link>
            </Button>
            <Button variant="outline" className="justify-start h-auto py-4" asChild>
              <Link to="/app/search">
                <Search className="mr-3 h-5 w-5 text-voltaire-blue" />
                <div className="text-left">
                  <div className="font-medium">Search</div>
                  <div className="text-xs text-gray-500">Parts, manuals, ATA</div>
                </div>
              </Link>
            </Button>
            <Button variant="outline" className="justify-start h-auto py-4" asChild>
              <Link to="/app/maintenance-program">
                <CalendarClock className="mr-3 h-5 w-5 text-voltaire-blue" />
                <div className="text-left">
                  <div className="font-medium">Maintenance</div>
                  <div className="text-xs text-gray-500">Phase schedule</div>
                </div>
              </Link>
            </Button>
            <Button variant="outline" className="justify-start h-auto py-4" asChild>
              <Link to="/app/admin/analytics">
                <TrendingUp className="mr-3 h-5 w-5 text-voltaire-blue" />
                <div className="text-left">
                  <div className="font-medium">View Reports</div>
                  <div className="text-xs text-gray-500">Analytics & usage</div>
                </div>
              </Link>
            </Button>
          </CardContent>
        </Card>

        {/* Manual Processing Status */}
        <Card>
          <CardHeader>
            <CardTitle>Manual Status</CardTitle>
            <CardDescription>Recent uploads and processing status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <ManualStatusItem
                name="HA-420 AMM (ALIM Rev J)"
                status="ready"
                pages={1842}
                updated="Updated 2 hours ago"
              />
              <ManualStatusItem
                name="GE Honda HF120 Engine CMM"
                status="ready"
                pages={1265}
                updated="Updated yesterday"
              />
              <ManualStatusItem
                name="Garmin G3000 Pilot's Guide"
                status="processing"
                pages={896}
                updated="Processing 62%"
              />
              <ManualStatusItem
                name="HA-420 IPC (Illustrated Parts)"
                status="ready"
                pages={2340}
                updated="Updated 3 days ago"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  change,
  changeType,
  icon: Icon,
}: {
  title: string
  value: string
  change: string
  changeType: 'positive' | 'negative' | 'neutral'
  icon: React.ElementType
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
            <p className={`mt-1 text-sm ${
              changeType === 'positive' ? 'text-green-600' :
              changeType === 'negative' ? 'text-red-600' :
              'text-gray-500'
            }`}>
              {change}
            </p>
          </div>
          <div className="rounded-full bg-navy-900/10 p-3">
            <Icon className="h-6 w-6 text-navy-900" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function ManualStatusItem({
  name,
  status,
  pages,
  updated,
}: {
  name: string
  status: 'ready' | 'processing' | 'error'
  pages: number
  updated: string
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div className="flex items-center gap-3">
        <div className={`rounded-full p-1.5 ${
          status === 'ready' ? 'bg-green-100' :
          status === 'processing' ? 'bg-yellow-100' :
          'bg-red-100'
        }`}>
          {status === 'ready' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
          {status === 'processing' && <Clock className="h-4 w-4 text-yellow-600" />}
          {status === 'error' && <AlertCircle className="h-4 w-4 text-red-600" />}
        </div>
        <div>
          <p className="font-medium text-gray-900">{name}</p>
          <p className="text-xs text-gray-500">{updated}</p>
        </div>
      </div>
      <Badge variant={
        status === 'ready' ? 'success' :
        status === 'processing' ? 'warning' :
        'destructive'
      }>
        {status === 'ready' ? `${pages} pages` :
         status === 'processing' ? 'Processing' :
         'Error'}
      </Badge>
    </div>
  )
}
